import React, { useEffect, useRef } from "react";
import { View, StyleSheet, Dimensions, Animated, Easing } from "react-native";
import { Image } from "expo-image";

const { width, height } = Dimensions.get("window");

type AnimatedSplashScreenProps = {
  onFinish: () => void;
  isReady: boolean;
};

/**
 * AnimatedSplashScreen component
 * 
 * Displays an animated splash screen with Oma, Opa, and the Staatsen App text
 * Uses smooth animations to fade in elements sequentially
 * Compatible with Expo Go (uses React Native Animated API)
 * 
 * @param onFinish - Callback when animation is complete
 * @param isReady - Whether the app is ready to show (after auth check, etc.)
 */
export const AnimatedSplashScreen: React.FC<AnimatedSplashScreenProps> = ({
  onFinish,
  isReady,
}) => {
  // Animation values using React Native Animated API
  const opaOpacity = useRef(new Animated.Value(0)).current;
  const opaTranslateY = useRef(new Animated.Value(-50)).current;
  
  const omaOpacity = useRef(new Animated.Value(0)).current;
  const omaTranslateY = useRef(new Animated.Value(50)).current;
  
  const textOpacity = useRef(new Animated.Value(0)).current;
  const textScale = useRef(new Animated.Value(0.8)).current;
  
  const containerOpacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    // Opa enters from top
    Animated.parallel([
      Animated.timing(opaOpacity, {
        toValue: 1,
        duration: 700,
        delay: 200,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.spring(opaTranslateY, {
        toValue: 0,
        delay: 200,
        tension: 40,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();

    // Oma enters from bottom
    Animated.parallel([
      Animated.timing(omaOpacity, {
        toValue: 1,
        duration: 700,
        delay: 500,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.spring(omaTranslateY, {
        toValue: 0,
        delay: 500,
        tension: 40,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();

    // Text appears and scales up
    Animated.parallel([
      Animated.timing(textOpacity, {
        toValue: 1,
        duration: 600,
        delay: 800,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.spring(textScale, {
        toValue: 1,
        delay: 800,
        tension: 40,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  useEffect(() => {
    if (isReady) {
      Animated.timing(containerOpacity, {
        toValue: 0,
        duration: 500,
        delay: 1500,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (finished) {
          onFinish();
        }
      });
    }
  }, [isReady]);

  return (
    <Animated.View style={[styles.container, { opacity: containerOpacity }]}>
      {/* White background */}
      <View style={styles.background} />

      {/* Content container */}
      <View style={styles.content}>
        {/* Opa (top) - fills top portion */}
        <Animated.View
          style={[
            styles.imageContainer,
            {
              opacity: opaOpacity,
              transform: [{ translateY: opaTranslateY }],
            },
          ]}
        >
          <Image
            source={require("@/assets/splashscreen_images/opa_schets_zonder_achtergrond.svg")}
            style={styles.opaImage}
            contentFit="cover"
            cachePolicy="memory-disk"
          />
        </Animated.View>

        {/* Oma (bottom) - fills bottom portion */}
        <Animated.View
          style={[
            styles.imageContainer,
            {
              opacity: omaOpacity,
              transform: [{ translateY: omaTranslateY }],
            },
          ]}
        >
          <Image
            source={require("@/assets/splashscreen_images/oma_zonder_achtergrond.svg")}
            style={styles.omaImage}
            contentFit="cover"
            cachePolicy="memory-disk"
          />
        </Animated.View>
      </View>

      {/* Text (centered) - overlays in the middle with shadow for depth */}
      <Animated.View
        style={[
          styles.textContainer,
          {
            opacity: textOpacity,
            transform: [{ scale: textScale }],
            shadowColor: "#7C3AED",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 8,
          },
        ]}
      >
        <Image
          source={require("@/assets/splashscreen_images/text_splashscreen.svg")}
          style={styles.textImage}
          contentFit="contain"
          cachePolicy="memory-disk"
        />
      </Animated.View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
  },
  background: {
    flex: 1,
    backgroundColor: "#FFFFFF", // White background
  },
  content: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "space-between",
    alignItems: "center",
  },
  imageContainer: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  opaImage: {
    width: width,
    height: height * 0.4, // Fill top 40% of screen
  },
  omaImage: {
    width: width,
    height: height * 0.4, // Fill bottom 40% of screen
  },
  textContainer: {
    position: "absolute",
    top: "30%", // Moved up 20% from center (was 50%)
    left: 0,
    right: 0,
    transform: [{ translateY: -155 }], // Center vertically (half of new height)
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 0,
  },
  textImage: {
    width: width, // Full width for maximum size
    height: 310, // 15% larger (270 * 1.15 = 310.5)
  },
});

